/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
// Entry file
/* eslint-disable @atlaskit/editor/no-re-export */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { bindAll } from 'bind-event-listener';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import { cssMap, css, jsx } from '@atlaskit/css';
import { AssistiveText } from '@atlaskit/editor-common/AssistiveText';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import Heading from '@atlaskit/heading/heading';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import type { EditorAgent } from '../../../utils/agents';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { CommandPaletteLozenge } from '../../components/CommandPaletteLozenge/CommandPaletteLozenge';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { UsePageKnowledgeToggleFooter } from '../../components/Footer/UsePageKnowledgeToggleFooter';
import type { PromptEditor } from '../../components/PromptEditorWrapper/PromptEditorWrapper';
import { PromptForm } from '../../components/PromptForm/PromptForm';
import type { FocusInputMutableRefObject } from '../../components/PromptForm/useSetInputRef';
import { RefinementTag } from '../../components/RefinementTag/RefinementTag';
import { Suggestions } from '../../components/Suggestions/Suggestions';
import type { Suggestion } from '../../components/Suggestions/types';
import { useAgentRefinementTagProps } from '../../hooks/use-agent-refinement-tag-props';
import { AnnouncementBanners } from './AnnouncementBanners/AnnouncementBanners';
import type { AnnouncementBanner } from './AnnouncementBanners/AnnouncementBanners';
import { getNextSuggestion } from './getNextSuggestion';
import messages from './messages';
import { reduceSuggestionsIntoNestedSuggestions } from './reduceSuggestionsIntoNestedSuggestions';
export type { Suggestion } from '../../components/Suggestions/types';
export type {
	PromptEditor,
	PromptEditorProps,
} from '../../components/PromptEditorWrapper/PromptEditorWrapper';

export type { AnnouncementBanner };
export type { FocusInputRefType } from '../../components/PromptForm/useSetInputRef';

const columnLayout = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.050'),
});

/**
 * Scroll margin is used to ensure that the modal won't be
 * too close to the top of the editor, which may sometimes
 * cover the modal slightly.
 */
const scrollMarginStyles = css({ scrollMarginTop: token('space.400') });

const caretHiddenStyle = css({ caretColor: 'transparent' });

const PromptHeaderWrapperStyles = cssMap({
	default: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		paddingBottom: token('space.150'),
		// Use min-height to prevent the prompt header from growing out of the parent.
		minHeight: '0px',
		flexShrink: 0,
	},
});

const PromptHeaderWrapperTeam25Styles = cssMap({
	default: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.150'),
		// Use min-height to prevent the prompt header from growing out of the parent.
		minHeight: '0px',
		flexShrink: 0,
	},
});

const userInputFloatingContainerStyle = css({
	display: 'flex',
	flexDirection: 'column',
	paddingTop: token('space.0'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.0'),
});

type Props = {
	/**
	 * The agent selected for this experience.
	 */
	agent?: EditorAgent;
	/** List of announcement banners to show before the suggestions. */
	announcementBanners?: AnnouncementBanner[];
	appearance: EditorAppearance;

	/**
	 * Reference to the text input which we use to set focus
	 */
	focusInputRef: FocusInputMutableRefObject;
	/**
	 * Retrieves the suggestions based on the user text input.
	 *
	 * Does not get called when the user has selected a partial prompt.
	 */
	getSuggestions: (inputValue: string, agent?: EditorAgent) => Suggestion[];
	/**
	 * Whether to hide the legacy submit and cancel buttons.
	 */
	hideLegacySubmitCancelButtons?: boolean;
	inputADFValue?: JSONDocNode;
	/**
	 * The current value of the user text input.
	 */
	inputValue: string;
	/**
	 * Whether the AI Modal shows the new AIFC command palette experience.
	 */
	isAIFC?: boolean;
	/**
	 * Updates the value of the user text input.
	 */
	onADFUpdate?: (value: JSONDocNode) => void;
	/**
	 * Handles the behaviour when the user clicks the back button.
	 */
	onBack?: () => void;
	/**
	 * Handles the behaviour when the user presses the escape key.
	 */
	onEscapeKeyPressed: () => void;
	/**
	 * Updates the value of the user text input.
	 */
	onInputUpdate?: (value: string) => void;
	/**
	 * Handles when the user clicks the cross on the refinement tag.
	 */
	onRefinementTagRemove: () => void;
	/**
	 * Submits the user input text and partial prompt (if any) to the AI.
	 */
	onSubmit: () => void;
	/**
	 * These props determine whether to show the user a toggle to include
	 * knowledge from the current page in the AI response. This is true
	 * by default and only shown for free generate with general AI & rovo.
	 */
	pageKnowledgeProps?: { isEnabled: boolean; isVisible: boolean; onToggle: () => void };
	/**
	 * The placeholder text if there is no user text input
	 */
	placeholder: string;
	/**
	 * Label for the selected partial prompt.
	 */
	presetPromptLabel?: string;
	/**
	 * Editor to be used as input in prompt.
	 */
	PromptEditor: PromptEditor;
	/**
	 * Heading for the suggestion list
	 */
	suggestionsHeading?: string;
};

/**
 * This screen is used when the user is interacting with the AI command palette.
 *
 * ---
 *
 * If there is no preset prompt label, show the suggestions.
 * Otherwise, show the preset prompt label and no suggestions.
 */
export const UserInputCommandPalette = ({
	appearance,
	inputValue,
	inputADFValue,
	onInputUpdate,
	onADFUpdate,
	presetPromptLabel,
	onSubmit,
	onEscapeKeyPressed,
	onRefinementTagRemove,
	onBack,
	placeholder,
	getSuggestions,
	suggestionsHeading,
	focusInputRef,
	PromptEditor,
	agent,
	hideLegacySubmitCancelButtons,
	announcementBanners,
	pageKnowledgeProps,
	isAIFC,
}: Props): JSX.Element => {
	const promptWithSuggestionsRef = useRef<HTMLDivElement>(null);
	const { formatMessage } = useIntl();

	// There is a situation when using the arrow keys to navigate through the suggestions
	// where Prosemirror will move the caret to the end of the prompt input's placeholder
	// as the placeholder is done via a content-editable bit of text. This solution allows
	// the caret to not jump around while pressing 'down' while not having to edit the core
	// functionality of Prosemirror.
	const [isCaretHidden, setIsCaretHidden] = useState(false);

	const [isShowingMoreSuggestions, setIsShowingMoreSuggestions] = useState(false);

	/**
	 * Scroll prompt into view as suggestions are not always fully visible
	 * when PromptInput is autofocused.
	 *
	 * Note: We use useLayoutEffect here vs useEffect as it is synchronous and operates
	 * after the DOM has been updated with all mutations etc. This is important as
	 * Safari has a bug where it can not correctly calculate scroll positions and glitches
	 * out causing all content to disappear without it.
	 *
	 * Performance implications should be minimal as we are not affecting the rendering
	 * of content, but rather using it for calculations and triggering scroll events.
	 *
	 * https://kentcdodds.com/blog/useeffect-vs-uselayouteffect
	 *
	 * ᕕ( ᐛ )ᕗ
	 */
	useLayoutEffect(() => {
		// Add event listener to handle escape key press
		const { current: element } = promptWithSuggestionsRef;

		// If the element is not mounted, do nothing
		if (!element) {
			return;
		}

		/**
		 * KeyDown (as opposed to KeyPress) event is used to handle the escape key so that
		 * other escape key handlers will not override this behaviour.
		 * Additionally this aligns with other escape listeners e.g. for closing typeahead popup.
		 */
		const handleEscapeKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				onEscapeKeyPressed();
			}
		};

		/**
		 * Handle KeyUp event to prevent unexpected escape behaviours when
		 * there are other escape handlers outside of component (e.g. in product)
		 */
		const handleEscapeKeyUp = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
		};

		return bindAll(element, [
			{ type: 'keydown', listener: handleEscapeKeyDown },
			{ type: 'keyup', listener: handleEscapeKeyUp },
		]);
	}, [promptWithSuggestionsRef, onEscapeKeyPressed]);

	const getNestedSuggestions = useCallback(
		(input: string, presetLabel?: string) => {
			let nestedSuggestions = getSuggestions(input) ?? [];
			nestedSuggestions = nestedSuggestions.reduce(reduceSuggestionsIntoNestedSuggestions, []);

			if (presetLabel) {
				nestedSuggestions =
					nestedSuggestions.find((s) => s.nestingConfig?.parentTitle === presetLabel)
						?.childSuggestions ?? [];
			}

			const showMoreSuggestionIndex = nestedSuggestions.findIndex(
				(suggestion) => suggestion.value === 'show-more',
			);

			if (showMoreSuggestionIndex !== -1) {
				const originalOnSelect = nestedSuggestions[showMoreSuggestionIndex].onSelect;
				nestedSuggestions[showMoreSuggestionIndex].onSelect = () => {
					originalOnSelect();
					setIsShowingMoreSuggestions(true);
				};
			}

			return nestedSuggestions;
		},
		[getSuggestions],
	);

	const suggestions: Suggestion[] = useMemo(() => {
		if (agent) {
			return getSuggestions(inputValue, agent);
		}

		if (!inputValue.length) {
			return getNestedSuggestions(inputValue, presetPromptLabel);
		}

		return getSuggestions(inputValue) ?? [];
	}, [agent, getSuggestions, getNestedSuggestions, inputValue, presetPromptLabel]);

	const allSuggestions: Suggestion[] = useMemo(() => {
		if (isAIFC) {
			return getNestedSuggestions('', presetPromptLabel);
		}
		return [];
	}, [getNestedSuggestions, presetPromptLabel, isAIFC]);

	const hideNestedTag = useMemo(() => {
		return presetPromptLabel && !!suggestions.length;
	}, [suggestions, presetPromptLabel]);

	const agentRefinementTagProps = useAgentRefinementTagProps(agent);
	const refinementTagRef = useRef<HTMLDivElement>(null);

	const tag: React.ReactNode | undefined = useMemo(() => {
		return presetPromptLabel === undefined ? undefined : (
			<RefinementTag
				// Ignored via go/ees005
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...agentRefinementTagProps}
				label={presetPromptLabel}
				ref={refinementTagRef}
				onClick={onRefinementTagRemove}
			/>
		);
	}, [agentRefinementTagProps, presetPromptLabel, onRefinementTagRemove, refinementTagRef]);

	const suggestionValues = useMemo(() => {
		return (suggestions || []).map((suggestion) => suggestion.value);
	}, [suggestions]);
	const [currentSuggestion, _setCurrentSuggestion] = useState<string>(
		suggestionValues.length > 0 ? suggestionValues[0] : '',
	);

	/**
	 * Handles the behaviour for when a user changes the suggestion.
	 *
	 * At the moment this is used to highlight the entire editor document for
	 * relevant suggestions.
	 */
	const handleSuggestionChanged = useCallback(
		(suggestion: string) => {
			const suggestionObject = suggestions.find((s) => s.value === suggestion);
			suggestionObject?.onSuggestionFocused();
		},
		[suggestions],
	);

	const setCurrentSuggestion = useCallback(
		(newValue: string) => {
			handleSuggestionChanged(newValue);
			_setCurrentSuggestion(newValue);
		},
		[_setCurrentSuggestion, handleSuggestionChanged],
	);

	// Select first option by default
	useEffect(() => {
		if (suggestionValues.includes(currentSuggestion) || suggestionValues.length === 0) {
			return;
		}

		setCurrentSuggestion(suggestionValues[0]);
	}, [currentSuggestion, suggestionValues, setCurrentSuggestion]);

	// Calculate visible suggestions list
	const visibleSuggestionValues = useMemo(() => {
		const allSuggestions = suggestionValues.filter((suggestion) => suggestion !== 'show-more');

		if (isShowingMoreSuggestions || inputValue.length > 0) {
			return allSuggestions;
		}

		const agentSuggestions = suggestionValues.filter((suggestion) =>
			suggestion.startsWith('agent:'),
		);

		const minNumOfGeneralSuggestions = 4;
		const maximumResultCount = 9;

		if (
			agentSuggestions.length === 0 ||
			allSuggestions.length <= maximumResultCount ||
			// If we only have the min. number of suggestions,
			// we return the full list because we don't need to show the show more button
			allSuggestions.length - agentSuggestions.length <= minNumOfGeneralSuggestions
		) {
			return allSuggestions;
		}

		// The maximum number of suggestions is 8 when agents are present
		// We take the extra 1 so that we can put the show more button in place
		// of the last expected suggestion.
		let remainingSuggestions = maximumResultCount - agentSuggestions.length - 1;

		// If the user favourites enough agents, the resulting count will be negative and
		// force the entire list to show. We want to encourage it to limit the slicing to 1
		// item in this case.
		if (remainingSuggestions < minNumOfGeneralSuggestions) {
			remainingSuggestions = minNumOfGeneralSuggestions;
		}

		return [...suggestionValues.slice(0, remainingSuggestions), 'show-more', ...agentSuggestions];
	}, [inputValue.length, isShowingMoreSuggestions, suggestionValues]);

	const visibleSuggestions = useMemo(() => {
		const output = suggestions.filter((suggestion) =>
			visibleSuggestionValues.includes(suggestion.value),
		);

		return output;
	}, [suggestions, visibleSuggestionValues]);

	const onArrowKeyDown = useCallback(
		(event: React.KeyboardEvent): void => {
			// Ignore if any key modifiers are held
			if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
				return;
			}

			// If theres one or fewer suggestions, no need to navigate and allow normal
			// multiline text traversal
			if (visibleSuggestionValues.length <= 1) {
				return;
			}

			switch (event.key) {
				case 'ArrowUp':
				case 'ArrowDown': {
					setIsCaretHidden(true);

					const nextSuggestion = getNextSuggestion(
						visibleSuggestionValues,
						currentSuggestion,
						event.key,
					);
					setCurrentSuggestion(nextSuggestion);

					event.preventDefault();
					event.stopPropagation();
					break;
				}
				default: {
					return;
				}
			}
		},
		[currentSuggestion, setCurrentSuggestion, visibleSuggestionValues],
	);

	const debouncedSetIsCaretHidden = useMemo(
		() =>
			debounce(() => {
				setIsCaretHidden(false);
			}, 300),
		[],
	);

	const onArrowKeyUp = useCallback(() => {
		debouncedSetIsCaretHidden();
	}, [debouncedSetIsCaretHidden]);

	const setSuggestion = useCallback(
		(suggestion: Suggestion): void => {
			setCurrentSuggestion(suggestion.value);
		},
		[setCurrentSuggestion],
	);

	const onFormSubmit = useCallback(() => {
		if (currentSuggestion === '' || suggestions?.length === 0) {
			onSubmit();
			return;
		}

		const suggestion = (suggestions || []).find(
			(suggestion) => suggestion.value === currentSuggestion,
		);

		if (suggestion) {
			suggestion.onSelect();
		}

		setCurrentSuggestion('');
	}, [currentSuggestion, suggestions, setCurrentSuggestion, onSubmit]);

	// if a refinement tag like brainstorm was selected, we continue showing these legacy buttons
	const showLegacySubmitCancelButtons =
		!hideLegacySubmitCancelButtons && Boolean(presetPromptLabel && !agent);

	const promptPlaceholder = useMemo(() => {
		const defaultPlaceholder = placeholder;

		if (agent) {
			return formatMessage(messages.rovoAgentsPalettePlaceholder);
		}

		if (tag) {
			return defaultPlaceholder;
		}

		return defaultPlaceholder;
	}, [agent, formatMessage, placeholder, tag]);

	const promptForm = (
		<Flex
			testId="prompt-header-wrapper"
			xcss={
				fg('ai-smart-button-team-25')
					? PromptHeaderWrapperTeam25Styles.default
					: PromptHeaderWrapperStyles.default
			}
		>
			<PromptForm
				type="user-input"
				showButtons={showLegacySubmitCancelButtons}
				placeholder={
					isAIFC ? formatMessage(messages.commandPaletteV2Placeholder) : promptPlaceholder
				}
				value={inputValue}
				adfValue={inputADFValue}
				onFormSubmit={onFormSubmit}
				onInputChange={onInputUpdate}
				onADFChange={onADFUpdate}
				tag={hideNestedTag ? undefined : tag}
				focusInputRef={focusInputRef}
				showLogo={false}
				onInputKeyDown={onArrowKeyDown}
				onInputKeyUp={onArrowKeyUp}
				PromptEditor={PromptEditor}
				enableLinks={true}
				showBack={!!presetPromptLabel}
				onBack={onBack}
				refinementTagRef={refinementTagRef}
				isAIFC={isAIFC}
			/>
		</Flex>
	);

	return (
		// Need div for css composition
		// https://atlassian.design/components/eslint-plugin-design-system/usage#prefer-primitives
		// eslint-disable-next-line
		<div
			ref={promptWithSuggestionsRef}
			data-testid="user-input-command-palette-screen"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={`user-input-command-palette-screen ${AI_MODAL_SCREEN_CLASS}`}
			css={[columnLayout, scrollMarginStyles, isCaretHidden && caretHiddenStyle]}
		>
			{
				<Heading size="large">
					<VisuallyHidden>{formatMessage(messages.commandPaletteAriaName)}</VisuallyHidden>
				</Heading>
			}
			<FloatingContainer
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				css={[userInputFloatingContainerStyle]}
				disablePadding
				data-testid="prompt-form-screen"
				rainbowBorder={false}
				enableMaxHeight={true}
				isAIFC={isAIFC}
			>
				{promptForm}
				{isAIFC ? (
					<CommandPaletteLozenge
						onFormSubmit={onFormSubmit}
						inputValue={inputValue}
						suggestions={allSuggestions}
						setSuggestion={setSuggestion}
						showButton={!!!presetPromptLabel}
					/>
				) : (
					<React.Fragment>
						{announcementBanners && <AnnouncementBanners banners={announcementBanners} />}
						<AssistiveText
							assistiveText={formatMessage(messages.suggestionsUpdated, {
								numberOfSuggestions: visibleSuggestions.length,
							})}
							isInFocus={visibleSuggestions.length > 0}
							id="suggestions-updated"
							data-testid="suggestions-updated"
						/>
						{suggestions || inputValue ? (
							<Suggestions
								appearance={appearance}
								suggestions={visibleSuggestions}
								suggestionsHeading={suggestionsHeading}
								value={currentSuggestion}
								setSuggestion={setSuggestion}
								hideGroupHeading={!!agent}
							/>
						) : null}
						{pageKnowledgeProps?.isVisible && (
							<UsePageKnowledgeToggleFooter
								onChange={pageKnowledgeProps.onToggle}
								isChecked={pageKnowledgeProps.isEnabled}
							/>
						)}
					</React.Fragment>
				)}
			</FloatingContainer>
		</div>
	);
};
